// _forms/_shared/utils/formatters.js
/**
 * Formateadores puros - Sin dependencias de React
 * Todas las funciones reciben datos y devuelven strings formateados
 */
export const formatters = {
  /**
   * Formatear fecha a DD-MM-YYYY
   */
  formatDateForInput: (dateValue) => {
    if (!dateValue) return '';
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    if (typeof dateValue === 'string') {
      try {
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      } catch {
        return '';
      }
    }
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  },

  formatDate: (dateString, fallback = 'No definida') => {
    if (!dateString) return fallback;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  },

  /**
   * Formatear fecha a formato largo (ej: 15 de Julio, 2024)
   */
  formatDateLong: (dateString, fallback = 'No definida') => {
    if (!dateString) return fallback;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('es-ES', options);
  },

  /**
   * Formatear moneda
   */
  formatCurrency: (amount, currency = 'Bs.', decimals = 2) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `0 ${currency}`;
    }
    
    const numAmount = parseFloat(amount);
    
    // Verificar si tiene decimales significativos (no es .00)
    const hasDecimals = numAmount % 1 !== 0;
    
    // Si tiene decimales, mostrar con 2 decimales. Si no, mostrar entero
    const formatted = hasDecimals 
      ? numAmount.toFixed(decimals) 
      : numAmount.toFixed(0);
    
    return `${formatted} ${currency}`;
  },

  /**
   * Formatear número
   */
  formatNumber: (number, decimals = 0) => {
    if (number === null || number === undefined || isNaN(number)) return '0';
    return parseFloat(number).toFixed(decimals);
  },

  /**
   * Obtener etiqueta de competidor según número
   */
  getCompetitorLabel: (numberOfCompetitors) => {
    const num = parseInt(numberOfCompetitors) || 1;
    switch (num) {
      case 1: return 'Individual';
      case 2: return 'Duplas';
      case 3: return 'Tríos';
      case 4: return 'Equipos de 4';
      case 5: return 'Equipos de 5';
      case 6: return 'Equipos de 6';
      default: return `Equipos de ${num}`;
    }
  },

  /**
   * Calcular monto total con addons
   */
  calculateTotalAmount: (amount, numberOfCompetitors, addons = [], selectedAddons = {}) => {
    let amountPerPerson = parseFloat(amount) || 0;
    const extraPorAddons = addons
      .filter(addon => selectedAddons[addon.id])
      .reduce((sum, addon) => sum + (parseFloat(addon.extra_amount) || 0), 0);
    amountPerPerson += extraPorAddons;
    const numCompetitors = parseInt(numberOfCompetitors) || 1;
    return (amountPerPerson * numCompetitors).toFixed(2);
  },

  /**
   * Convertir Bs a USD
   */
  convertToUSD: (amountInBs, conversionRate = 6.96) => {
    if (!amountInBs || isNaN(amountInBs)) return '0.00';
    const rate = parseFloat(conversionRate) || 6.96;
    return (parseFloat(amountInBs) / rate).toFixed(2);
  },

  /**
   * Truncar texto
   */
  truncateText: (text, maxLength = 50, suffix = '...') => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + suffix;
  },

  /**
   * Capitalizar primera letra
   */
  capitalize: (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  },

  /**
   * Generar descripción de pago
   */
  generatePaymentDescription: (slug, packageTitle, name) => {
    let description = `${slug || 'evento'} - ${packageTitle || 'sin-paquete'}`;
    if (name) {
      description += ` - ${name}`;
    }
    return description.length > 40 ? description.substring(0, 40) : description;
  },

  /**
   * Obtener nombre completo de campos del formulario
   */
  getFullName: (camposActivos) => {
    if (!camposActivos || !Array.isArray(camposActivos)) return '';
    const nombre = camposActivos.find(campo =>
      campo.nombre?.toLowerCase().includes('nombre')
    )?.valor || '';
    const apellido = camposActivos.find(campo =>
      campo.nombre?.toLowerCase().includes('apellido')
    )?.valor || '';
    if (nombre && apellido) return `${nombre} ${apellido}`;
    return nombre || '';
  },

  /**
   * Extraer ID de una URL
   */
  extractIdFromUrl: (url) => {
    if (!url) return null;
    if (url.includes('/')) {
      const parts = url.split('/');
      return parts[parts.length - 1];
    }
    return url;
  }
};

export default formatters;